import axios from 'axios';
import {
  BASE_PATH,
  getApiHubGatewayPath,
  isApiHubEnabled,
  isApiHubV2,
  isBffEnabled
} from './config';

const AUTH_ENDPOINTS = new Set(['/login', '/relogin']);

const apiClient = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    let token = localStorage.getItem('authToken');

    if (!token) {
      const selectedLoginData = localStorage.getItem('selectedLoginData');
      if (selectedLoginData) {
        try {
          const parsed = JSON.parse(selectedLoginData);
          token = parsed.resources_id ? `token_${parsed.resources_id}` : null;
        } catch (error) {
          console.warn('Failed to parse selectedLoginData');
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  if (isBffEnabled() || isApiHubEnabled()) {
    config.headers['X-Request-Source'] = 'frontend';
    config.withCredentials = true;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

const normalizeEndpoint = (endpoint) => {
  if (!endpoint) {
    return '/';
  }
  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

const toHubApiName = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  const [path] = normalized.split('?');
  return path.replace(/^\/+/, '');
};

const parseEndpoint = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  const [, queryString] = normalized.split('?');
  const queryParams = {};

  if (queryString) {
    new URLSearchParams(queryString).forEach((value, key) => {
      queryParams[key] = value;
    });
  }

  return {
    api_name: toHubApiName(endpoint),
    queryParams
  };
};

const resolveDirectUrl = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  const [path] = normalized.split('?');

  if (!isApiHubEnabled() && isBffEnabled() && AUTH_ENDPOINTS.has(path)) {
    return `${BASE_PATH}/auth${path}${normalized.includes('?') ? `?${normalized.split('?')[1]}` : ''}`;
  }

  return `${BASE_PATH}${normalized}`;
};

const buildHubPayload = (apiName, data, queryParams = {}, config = {}, httpMethod = 'POST') => {
  const payload = {
    api_name: toHubApiName(apiName)
  };

  if (!isApiHubV2()) {
    payload.api_method = httpMethod.toLowerCase();
  }

  if (queryParams && typeof queryParams === 'object') {
    Object.assign(payload, queryParams);
  }

  if (config.params && typeof config.params === 'object') {
    Object.assign(payload, config.params);
  }

  if (data !== null && data !== undefined) {
    if (typeof data === 'object' && !Array.isArray(data)) {
      if (isApiHubV2()) {
        const { api_method: _apiMethod, api_token: _apiToken, ...rest } = data;
        Object.assign(payload, rest);
      } else {
        const { api_token: _apiToken, ...rest } = data;
        Object.assign(payload, rest);
      }
    } else {
      payload.api_body = data;
    }
  }

  return payload;
};

const hubRequest = async (httpMethod, apiName, data = null, queryParams = {}, config = {}) => {
  const payload = buildHubPayload(apiName, data, queryParams, config, httpMethod);
  const gatewayPath = getApiHubGatewayPath();
  const hubHttpMethod = isApiHubV2() ? 'post' : 'put';

  return apiClient.request({
    ...config,
    method: hubHttpMethod,
    url: gatewayPath,
    data: payload
  });
};

const directRequest = async (method, endpoint, data = null, config = {}) => {
  const url = resolveDirectUrl(endpoint);
  const requestConfig = { ...config };

  switch (method.toUpperCase()) {
    case 'GET':
      return apiClient.get(url, requestConfig);
    case 'POST':
      return apiClient.post(url, data, requestConfig);
    case 'PUT':
      return apiClient.put(url, data, requestConfig);
    case 'DELETE':
      return apiClient.delete(url, requestConfig);
    case 'PATCH':
      return apiClient.patch(url, data, requestConfig);
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
};

const request = async (method, endpoint, data = null, config = {}) => {
  if (isApiHubEnabled()) {
    const { api_name, queryParams } = parseEndpoint(endpoint);
    return hubRequest(method, api_name, data, queryParams, config);
  }

  return directRequest(method, endpoint, data, config);
};

const nbrApi = {
  get: (endpoint, config = {}) => request('GET', endpoint, null, config),
  post: (endpoint, data = {}, config = {}) => request('POST', endpoint, data, config),
  put: (endpoint, data = {}, config = {}) => request('PUT', endpoint, data, config),
  delete: (endpoint, config = {}) => request('DELETE', endpoint, null, config),
  patch: (endpoint, data = {}, config = {}) => request('PATCH', endpoint, data, config),
  request: (config = {}) => {
    const method = (config.method || 'GET').toUpperCase();
    const endpoint = config.url || '/';
    const data = config.data ?? null;
    const { url: _url, method: _method, data: _data, ...rest } = config;
    return request(method, endpoint, data, rest);
  }
};

export default nbrApi;
